import { useEffect, useState } from 'react'

import { MAX_ADJUSTMENT } from '@/services'
import { PosModel } from '@/types'
import { formatMoney } from '@/utils'

const Row = ({ label, value, tone }: { label: string; value: string; tone?: 'success' }) => (
  <div className="flex justify-between text-sm">
    <span className="text-muted-foreground">{label}</span>
    <span className={tone === 'success' ? 'tabular-nums text-success' : 'tabular-nums text-ink'}>{value}</span>
  </div>
)

interface CartTotalsProps {
  totals: PosModel.Totals
  taxRate: number
  /** What the order discount is, e.g. the member tier's (§7.3). */
  orderLabel?: string
  /** Round-off input (0–0.99); left out where the totals are read only. */
  onAdjustment?: (value: number) => void
  /** Products carry their own VAT on a device; the label shows a rate only when the register has one. */
  showRate?: boolean
}

export const CartTotals = ({ totals, taxRate, orderLabel, onAdjustment, showRate = true }: CartTotalsProps) => {
  const [text, setText] = useState(totals.adjustment ? String(totals.adjustment) : '')
  // A cleared or loaded cart resets the field.
  useEffect(() => {
    if (!totals.adjustment) setText('')
  }, [totals.adjustment])

  return (
    <div className="space-y-1.5">
      <Row label={`Subtotal · ${totals.itemCount} item${totals.itemCount === 1 ? '' : 's'}`} value={formatMoney(totals.subtotal)} />
      {totals.lineDiscounts > 0 && <Row label="Item discounts" value={`−${formatMoney(totals.lineDiscounts)}`} tone="success" />}
      {totals.orderDiscount > 0 && <Row label={orderLabel ?? 'Order discount'} value={`−${formatMoney(totals.orderDiscount)}`} tone="success" />}
      <Row label={showRate && taxRate ? `VAT ${Math.round(taxRate * 100)}%` : 'VAT'} value={formatMoney(totals.tax)} />
      {onAdjustment ? (
        <div className="flex items-center justify-between text-sm">
          <label htmlFor="cart-adjustment" className="text-muted-foreground">
            Adjustment
          </label>
          <span className="flex items-center gap-1 text-ink">
            −
            <input
              id="cart-adjustment"
              type="number"
              inputMode="decimal"
              min={0}
              max={MAX_ADJUSTMENT}
              step={0.01}
              placeholder="0.00"
              value={text}
              onChange={(e) => {
                setText(e.target.value)
                onAdjustment(parseFloat(e.target.value) || 0)
              }}
              onBlur={() => setText(totals.adjustment ? totals.adjustment.toFixed(2) : '')}
              title={`Round-off, up to ${MAX_ADJUSTMENT}`}
              className="h-7 w-20 rounded-xs border border-input bg-card px-2 text-right tabular-nums outline-none focus-visible:ring-2 focus-visible:ring-ring"
            />
          </span>
        </div>
      ) : (
        totals.adjustment > 0 && <Row label="Adjustment" value={`−${formatMoney(totals.adjustment)}`} />
      )}
    </div>
  )
}
